const SEARCH_HISTORY_KEY = 'previously_searched';
const SEARCH_RECENTLY_VIEWED_KEY = 'bogg_search_recently_viewed';
const MAX_RECENT_SEARCHES = 8;
const MAX_RECENTLY_VIEWED = 4;

const SearchHistoryManager = {
  getRecentSearches() {
    try {
      return JSON.parse(localStorage.getItem(SEARCH_HISTORY_KEY)) || [];
    } catch {
      return [];
    }
  },

  addRecentSearch(term) {
    if (!term || !term.trim()) return;
    const trimmed = term.trim();
    const searches = this.getRecentSearches().filter(
      (s) => s.toLowerCase() !== trimmed.toLowerCase()
    );
    searches.unshift(trimmed);
    if (searches.length > MAX_RECENT_SEARCHES) searches.length = MAX_RECENT_SEARCHES;
    try {
      localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(searches));
    } catch {
      // Storage full or unavailable
    }
  },

  getRecentlyViewed() {
    try {
      return JSON.parse(localStorage.getItem(SEARCH_RECENTLY_VIEWED_KEY)) || [];
    } catch {
      return [];
    }
  },

  addRecentlyViewed(product) {
    if (!product || !product.id) return;
    const items = this.getRecentlyViewed().filter((p) => p.id !== product.id);
    items.unshift(product);
    if (items.length > MAX_RECENTLY_VIEWED) items.length = MAX_RECENTLY_VIEWED;
    try {
      localStorage.setItem(SEARCH_RECENTLY_VIEWED_KEY, JSON.stringify(items));
    } catch {
      // Storage full or unavailable
    }
  },

  hasSearchHistory() {
    return this.getRecentSearches().length > 0;
  },

  hasRecentlyViewed() {
    return this.getRecentlyViewed().length > 0;
  },
};

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.emptyStateEl = this.querySelector('[data-search-empty-state]');

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('.predictive-search__results-wrapper')?.remove();
    }

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.showEmptyState();
      this.closeResults(true);
      return;
    }

    this.hideEmptyState();
    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    const searchTerm = this.getQuery().toLowerCase().trim();

    if (window.search_redirects && window.search_redirects.length > 0) {
      const matchingRedirect = window.search_redirects.find(
        (redirect) => redirect.term === searchTerm
      );
      if (matchingRedirect) {
        event.preventDefault();
        window.location.href = matchingRedirect.redirect_path;
        return;
      }
    }

    if (searchTerm.length) {
      SearchHistoryManager.addRecentSearch(searchTerm);
    }

    if (!searchTerm.length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
      this.showEmptyState();
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      this.showEmptyState();
      return;
    }

    this.hideEmptyState();

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(
      this.querySelectorAll('li[role="option"], button.predictive-search__item')
    ).filter((element) => element.offsetParent !== null);

    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );
    if (selectedOption) selectedOption.click();
  }

  async getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/ /g, '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    try {
      const response = await fetch(
        `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&resources[type]=product,query&resources[limit]=8&resources[limit_scope]=each&resources[options][fields]=title,body,tag,product_type,variants.title&section_id=predictive-search`,
        { signal: this.abortController.signal }
      );

      if (!response.ok) {
        this.close();
        throw new Error(response.status);
      }

      const text = await response.text();
      const resultsMarkup = new DOMParser()
        .parseFromString(text, 'text/html')
        .querySelector('#shopify-section-predictive-search').innerHTML;

      for (const instance of this.allPredictiveSearchInstances) {
        instance.cachedResults[queryKey] = resultsMarkup;
      }

      this.renderSearchResults(resultsMarkup);
    } catch (error) {
      if (error?.code === 20) return;
      this.close();
      throw error;
    }
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.hideEmptyState();
    this.bindProductClickTracking();
    this.bindSuggestionRedirects();
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(
      this.querySelector('[data-predictive-search-live-region-count-value]').textContent
    );
  }

  open() {
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
  }

  showEmptyState() {
    if (!this.emptyStateEl) return;
    this.emptyStateEl.classList.remove('hidden');
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.classList.add('hidden');
    }
    this.renderEmptyState();
  }

  hideEmptyState() {
    if (!this.emptyStateEl) return;
    this.emptyStateEl.classList.add('hidden');
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.classList.remove('hidden');
    }
  }

  renderEmptyState() {
    const hasHistory = SearchHistoryManager.hasSearchHistory();
    const hasViewed = SearchHistoryManager.hasRecentlyViewed();

    const popularTermsEl = this.emptyStateEl.querySelector('[data-search-popular-terms]');
    const prevSearchedEl = this.emptyStateEl.querySelector('[data-search-prev-searched]');
    const popularCollectionsEl = this.emptyStateEl.querySelector(
      '[data-search-popular-collections]'
    );
    const recentlyViewedEl = this.emptyStateEl.querySelector('[data-search-recently-viewed]');

    if (hasHistory) {
      if (popularTermsEl) popularTermsEl.classList.add('hidden');
      if (prevSearchedEl) {
        prevSearchedEl.classList.remove('hidden');
        this.renderPreviouslySearched(prevSearchedEl);
      }
    } else {
      if (popularTermsEl) popularTermsEl.classList.remove('hidden');
      if (prevSearchedEl) prevSearchedEl.classList.add('hidden');
    }

    if (hasViewed) {
      if (popularCollectionsEl) popularCollectionsEl.classList.add('hidden');
      if (recentlyViewedEl) {
        recentlyViewedEl.classList.remove('hidden');
        this.renderRecentlyViewed(recentlyViewedEl);
      }
    } else {
      if (popularCollectionsEl) popularCollectionsEl.classList.remove('hidden');
      if (recentlyViewedEl) recentlyViewedEl.classList.add('hidden');
    }

    PredictiveSearch.applySearchRedirects(this.emptyStateEl, '.search-empty__pill');
  }

  renderPreviouslySearched(container) {
    const pillsContainer = container.querySelector('[data-prev-searched-pills]');
    if (!pillsContainer) return;

    const searches = SearchHistoryManager.getRecentSearches();
    pillsContainer.innerHTML = searches
      .map(
        (term) =>
          `<a href="${routes.search_url}?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last" class="search-empty__pill">${this.escapeHtml(term)}</a>`
      )
      .join('');
  }

  stripVariantFromTitle(title) {
    const separators = [' – ', ' - '];
    for (const sep of separators) {
      const idx = title.indexOf(sep);
      if (idx > 0) return title.substring(0, idx).trim();
    }
    return title;
  }

  renderRecentlyViewed(container) {
    const grid = container.querySelector('[data-recently-viewed-grid]');
    if (!grid) return;

    const products = SearchHistoryManager.getRecentlyViewed();
    grid.innerHTML = products
      .map((product) => {
        const displayTitle = this.stripVariantFromTitle(product.title || '');
        return `
        <a href="${product.url}" class="search-empty__viewed-card grid__item">
          ${product.image ? `<div class="search-empty__viewed-image"><img src="${product.image}" alt="${this.escapeHtml(product.title)}" loading="lazy" width="200"></div>` : ''}
          <div class="search-empty__viewed-info">
            <div class="search-empty__viewed-title-row">
              <p class="search-empty__viewed-title">${this.escapeHtml(displayTitle)}</p>
              ${product.price ? `<p class="search-empty__viewed-price small-hide">${this.escapeHtml(product.price)}</p>` : ''}
            </div>
            ${product.type ? `<p class="search-empty__viewed-type">${this.escapeHtml(product.type)}</p>` : ''}
            ${product.color ? `<p class="search-empty__viewed-color">${this.escapeHtml(product.color)}</p>` : ''}
            ${product.price ? `<p class="search-empty__viewed-price medium-hide large-up-hide">${this.escapeHtml(product.price)}</p>` : ''}
          </div>
        </a>
      `;
      })
      .join('');
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  bindProductClickTracking() {
    const productItems = this.predictiveSearchResults.querySelectorAll(
      '[data-search-product-link]'
    );

    for (const item of productItems) {
      item.addEventListener('click', () => {
        const product = {
          id: item.dataset.productId,
          title: item.dataset.productTitle,
          url: item.dataset.productUrl,
          image: item.dataset.productImage || '',
          type: item.dataset.productType || '',
          color: item.dataset.productColor || '',
          price: item.dataset.productPrice || '',
        };
        SearchHistoryManager.addRecentlyViewed(product);

        const searchTerm = this.getQuery();
        if (searchTerm) {
          SearchHistoryManager.addRecentSearch(searchTerm);
        }
      });
    }
  }

  bindSuggestionRedirects() {
    PredictiveSearch.applySearchRedirects(
      this.predictiveSearchResults,
      '.predictive-search__suggestion-link'
    );
  }

  static applySearchRedirects(container, selector) {
    if (!window.search_redirects?.length || !container) return;
    const links = container.querySelectorAll(selector);
    for (const link of links) {
      if (link.hasAttribute('data-custom-link')) continue;
      link.addEventListener('click', (event) => {
        const term = link.textContent.toLowerCase().trim();
        const match = window.search_redirects.find((r) => r.term === term);
        if (match) {
          event.preventDefault();
          window.location.href = match.redirect_path;
        }
      });
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);
